import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Loading from "../../../components/CustomUI/Loading";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { withAuth } from "../../../components/HOC/withAuth";
import styles from "./CompanySettings.module.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";
const PAGE_PATH = "/account/company-settings";

const emptyForm = () => ({
  name: "",
  prefix: "",
  email: "",
  phone: "",
  address: "",
  taxNumber: "",
  taxOffice: "",
});

const CompanySettings = () => {
  useTitle("Rovion MES - Firma Ayarları");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const isAdmin = currentUser?.role?.toLowerCase() === "admin";
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      PAGE_PATH,
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [formData, setFormData] = useState(emptyForm());
  const [systemCompanies, setSystemCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    currentUser?.companyId || "",
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchCompany = useCallback(async () => {
    const companyId = isAdmin ? selectedCompanyId : currentUser?.companyId;
    if (!companyId) {
      setFormData(emptyForm());
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const params = isAdmin ? { companyId } : undefined;
      const { data } = await axios.get(
        `${API_URL}/api/system-companies/me/company`,
        { ...authHeaders(), params },
      );
      const c = data.company || {};
      setFormData({
        name: c.name || "",
        prefix: c.prefix || "",
        email: c.email || "",
        phone: c.phone || "",
        address: c.address || "",
        taxNumber: c.taxNumber || "",
        taxOffice: c.taxOffice || "",
      });
    } catch (error) {
      addToast(
        error.response?.data?.message || "Firma bilgileri yüklenemedi",
        "danger",
      );
      setFormData(emptyForm());
    } finally {
      setLoading(false);
    }
  }, [
    authHeaders,
    addToast,
    isAdmin,
    selectedCompanyId,
    currentUser?.companyId,
  ]);

  useEffect(() => {
    if (!isAdmin) return;
    const loadCompanies = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/system-companies`,
          authHeaders(),
        );
        const companies = data.companies || data || [];
        setSystemCompanies(companies);
        if (!selectedCompanyId && companies.length > 0) {
          const fallback =
            currentUser?.companyId || companies[0]._id || companies[0].id;
          setSelectedCompanyId(fallback || "");
        }
      } catch {
        addToast("Sistem firmaları yüklenemedi", "danger");
      }
    };
    loadCompanies();
  }, [isAdmin, authHeaders, addToast, currentUser?.companyId]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleSave = async () => {
    if (!canWrite) return;
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const address = formData.address.trim();
    if (!name || !email || !phone || !address) {
      addToast("Firma adı, e-posta, telefon ve adres zorunludur", "danger");
      return;
    }

    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API_URL}/api/system-companies/me/company`,
        {
          ...(isAdmin && selectedCompanyId
            ? { companyId: selectedCompanyId }
            : {}),
          name,
          email,
          phone,
          address,
          taxNumber: formData.taxNumber.trim(),
          taxOffice: formData.taxOffice.trim(),
          prefix: formData.prefix.trim(),
        },
        authHeaders(),
      );
      const c = data.company || {};
      setFormData((prev) => ({
        ...prev,
        name: c.name || name,
        email: c.email || email,
        phone: c.phone || phone,
        address: c.address || address,
        taxNumber: c.taxNumber ?? formData.taxNumber,
        taxOffice: c.taxOffice ?? formData.taxOffice,
        prefix: c.prefix ?? formData.prefix,
      }));
      addToast(data.message || "Firma bilgileri kaydedildi", "success");
    } catch (error) {
      addToast(
        error.response?.data?.message || "Firma bilgileri kaydedilemedi",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };

  const setField = (key) => (value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  return (
    <AppPage
      title="Firma Ayarları"
      actions={
        canWrite ? (
          <Button
            appearance="primary-animated"
            onClick={handleSave}
            loading={saving}
            disabled={loading}
          >
            Kaydet
          </Button>
        ) : null
      }
    >
      <CardContainer title="Firma Bilgileri" className={styles.form}>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Loading />
          </div>
        ) : (
          <div className={styles.grid}>
            {isAdmin && systemCompanies.length > 0 && (
              <div className={styles.full}>
                <Label className="mb-0">Sistem Firması</Label>
                <Select
                  unique
                  options={systemCompanies.map((c) => ({
                    value: String(c._id || c.id),
                    label: c.name || c.code || "Firma",
                  }))}
                  selected={selectedCompanyId ? [selectedCompanyId] : []}
                  onSelected={(values) =>
                    setSelectedCompanyId(values?.[0]?.value || "")
                  }
                  placeholder="Firma seçin"
                />
              </div>
            )}
            <div>
              <Label className="mb-0" required>
                Firma Adı
              </Label>
              <Input
                placeholder="Firma adı"
                value={formData.name}
                onChange={setField("name")}
                disabled={!canWrite}
              />
            </div>
            <div>
              <Label className="mb-0">Firma Öneki</Label>
              <Input
                placeholder="Örn: NEF"
                value={formData.prefix}
                onChange={(v) => setField("prefix")(String(v || "").toUpperCase())}
                disabled={!canWrite}
              />
            </div>
            <div>
              <Label className="mb-0" required>
                E-posta
              </Label>
              <Input
                type="email"
                placeholder="info@firma.com"
                value={formData.email}
                onChange={setField("email")}
                disabled={!canWrite}
              />
            </div>
            <div>
              <Label className="mb-0" required>
                Telefon
              </Label>
              <Input
                placeholder="Telefon"
                value={formData.phone}
                onChange={setField("phone")}
                disabled={!canWrite}
              />
            </div>
            <div>
              <Label className="mb-0">Vergi Numarası</Label>
              <Input
                placeholder="Vergi numarası"
                value={formData.taxNumber}
                onChange={setField("taxNumber")}
                disabled={!canWrite}
              />
            </div>
            <div>
              <Label className="mb-0">Vergi Dairesi</Label>
              <Input
                placeholder="Vergi dairesi"
                value={formData.taxOffice}
                onChange={setField("taxOffice")}
                disabled={!canWrite}
              />
            </div>
            <div className={styles.full}>
              <Label className="mb-0" required>
                Adres
              </Label>
              <Input
                type="textarea"
                rows={3}
                placeholder="Adres"
                value={formData.address}
                onChange={setField("address")}
                disabled={!canWrite}
              />
            </div>
          </div>
        )}
      </CardContainer>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(CompanySettings);
